import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { PhxConstants } from '@common';
import { PhxModalComponent } from '@common/components/phx-modal/phx-modal.component';
import { BaseComponentOnDestroy } from '@common/epics/base-component-on-destroy';
import { PhxButton, PhxFormControlLayoutType } from '@common/model';
import { FormBuilder, FormControl, FormGroup } from '@common/ngx-strongly-typed-forms/model';
import { AuthService } from '@common/services/auth.service';
import { CoreCommissionFormService } from '@workorder/services';

import { ControlFieldAccessibility, TFConstant } from '../../control-field-accessibility';
import { IJobOwner, IReadOnlyStorage } from '../../models';

export interface IOverrideCommissionRate {
  CalculatedCommissionRatePercentage: number;
  OverridenCommissionRatePercentage: number;
  OverrideReason: string;
};

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'tr[app-workorder-commission-row]',
  templateUrl: './workorder-commission-row.component.html',
  styleUrls: ['./workorder-commission-row.component.less'],
  standalone: false
})
export class WorkorderCommissionRowComponent extends BaseComponentOnDestroy implements OnInit {

  @Input() inputFormGroup: FormGroup<IJobOwner>;
  @Input() readOnlyStorage: IReadOnlyStorage;
  @Input() useCommissionMarginSplitPercentage: boolean;
  @Input() index: number;
  @ViewChild('modalCommissionRateOverride') modalCommissionRateOverride: PhxModalComponent;

  phxConstants = PhxConstants;

  layoutType: typeof PhxFormControlLayoutType;

  overrideFormGroup: FormGroup<IOverrideCommissionRate>;

  overrideButtons: PhxButton[] = [
    {
      icon: null,
      tooltip: 'Update Rate',
      btnType: 'primary',
      disabled: () => this.overrideFormGroup.invalid,
      action: () => {
        const currentUser = this.authService.currentUser.Profiles.find(x => x.ProfileId === this.authService.currentProfile.Id && x.InstanceName === this.authService.currentProfile.TenantName);

        this.inputFormGroup.patchValue({
          DisplayCommissionRatePercentage: +this.overrideFormGroup.value.OverridenCommissionRatePercentage,
          CommissionRatePercentage: +this.overrideFormGroup.value.OverridenCommissionRatePercentage,
          IsOverridden: true,
          OverrideReason: this.overrideFormGroup.value.OverrideReason,
          OverrideByUserProfileId: this.authService.currentProfile.Id,
          OverrideByFullName: currentUser.FirstName + ' ' + currentUser.LastName,
          OverrideDatetime: new Date()
        });

        this.closeModalCommissionRateOverride();
      },
    },
    {
      icon: null,
      tooltip: 'Reset',
      btnType: 'default',
      disabled: () => !this.inputFormGroup.value.IsOverridden,
      action: () => {
        this.inputFormGroup.patchValue({
          DisplayCommissionRatePercentage: this.overrideFormGroup.value.CalculatedCommissionRatePercentage,
          CommissionRatePercentage: this.overrideFormGroup.value.CalculatedCommissionRatePercentage,
          IsOverridden: false,
          OverrideReason: null,
          OverrideByUserProfileId: null,
          OverrideByFullName: null,
          OverrideDatetime: null
        });

        this.closeModalCommissionRateOverride();
      }
    },
    {
      icon: null,
      tooltip: 'Cancel',
      btnType: 'default',
      action: () => this.closeModalCommissionRateOverride()
    }
  ];

  constructor(private readonly authService: AuthService,
    private readonly coreCommissionFormService: CoreCommissionFormService,
    private readonly fb: FormBuilder
  ) {
    super();

    this.layoutType = PhxFormControlLayoutType;
  }

  get commissionRateHeaderIdFormControl(): FormControl<number> {
    return this.inputFormGroup.get('CommissionRateHeaderId') as FormControl<number>;
  }

  get commissionRateHeaderIdChange$() {
    return this.commissionRateHeaderIdFormControl.valueChanges;
  }

  get showCommissionRateColumn(): boolean {
    return this.coreCommissionFormService.showCommissionRateColumn;
  }

  ngOnInit() {
    this.setupFormListeners();
  }

  private setupFormListeners() {
    this.commissionRateHeaderIdChange$.pipe(
      filter(() => this.readOnlyStorage.IsCommissionsEditable),
      distinctUntilChanged(),
      takeUntil(this.isDestroyed$),
    ).subscribe((value: number) => {
      const commissionRateResult = this.coreCommissionFormService.getCommissionRatePercentage(value, false, null, this.inputFormGroup.value.CommissionRates);

      this.inputFormGroup.patchValue({
        DisplayCommissionRatePercentage: commissionRateResult?.ratePercentage,
        CommissionRatePercentage: commissionRateResult?.isCommissionRateProgram ? commissionRateResult.ratePercentage : null,
        MarginSplitPercentage: null,
        IsOverridden: false,
        OverrideReason: null,
        OverrideByUserProfileId: null,
        OverrideByFullName: null,
        OverrideDatetime: null
      });
    });
  }

  isMarginSplitPercentageInvalid(): boolean {
    return !!this.coreCommissionFormService.formGroup.controls.WorkOrderVersionCommissions.errors?.maxMarginSplitPercentageTotal;
  }

  checkPtFiledAccessibility(modelPrefix: string, fieldName: string): TFConstant {
    return ControlFieldAccessibility.ptFieldViewEventOnChangeStatusId(modelPrefix, fieldName, this.authService);
  }

  overrideCommissionRate() {
    let calculatedCommissionRatePercentage: number;
    let overridenCommissionRatePercentage: number;
    let overrideReason: string;

    if (this.inputFormGroup.value.IsOverridden) {
      calculatedCommissionRatePercentage = this.coreCommissionFormService
        .getCommissionRatePercentage(this.inputFormGroup.value.CommissionRateHeaderId, false, null, this.inputFormGroup.value.CommissionRates)
        ?.ratePercentage;

      overridenCommissionRatePercentage = this.inputFormGroup.value.CommissionRatePercentage;
      overrideReason = this.inputFormGroup.value.OverrideReason;
    }
    else {
      calculatedCommissionRatePercentage = this.inputFormGroup.value.CommissionRatePercentage;
    }

    this.overrideFormGroup = this.fb.group<IOverrideCommissionRate>({
      CalculatedCommissionRatePercentage: [calculatedCommissionRatePercentage],
      OverridenCommissionRatePercentage: [overridenCommissionRatePercentage, [Validators.required]],
      OverrideReason: [overrideReason, [Validators.required]]
    });

    this.modalCommissionRateOverride.show();
  }

  closeModalCommissionRateOverride() {
    this.overrideFormGroup = null;
    this.modalCommissionRateOverride.hide();
  }

}
